import type { CraftingGrid, GeneratedItem, ItemId, RecipeRecord, RecipeType, SlotIndex } from '@/types/domain';
import { normalize, makeKey } from '@/engine/RecipeNormalizer';
import { customItemId } from '@/utils/id';

export interface SeedSpec {
  grid: CraftingGrid;
  result: Omit<
    GeneratedItem,
    'id' | 'discoveredAt' | 'ingredientIds' | 'ingredientPositions' | 'parentRecipeIds' | 'traits' | 'isVanilla'
  > & { fixedId?: ItemId };
}

function build(grid: CraftingGrid, item: SeedSpec['result']): { item: GeneratedItem; record: RecipeRecord } {
  const type: RecipeType = item.recipeType;
  const norm = normalize(grid, type);
  const ingredientIds = norm.ingredientIds;
  const { fixedId, ...fields } = item;
  const ingredientPositions: (SlotIndex | null)[] = ingredientIds.map((id) => {
    if (type === 'shapeless') return null;
    const idx = grid.findIndex((c) => c && c.itemId === id);
    return idx >= 0 ? (idx as SlotIndex) : null;
  });
  const id = fixedId || customItemId();
  const now = Date.now();
  const full: GeneratedItem = {
    ...(fields as Omit<GeneratedItem, 'id' | 'discoveredAt' | 'ingredientIds' | 'ingredientPositions' | 'parentRecipeIds' | 'traits' | 'isVanilla'>),
    id,
    discoveredAt: now,
    ingredientIds,
    ingredientPositions,
    traits: [...(item.inheritedTraits || []), ...(item.newTraits || [])],
    parentRecipeIds: [],
    isVanilla: false,
  };
  const record: RecipeRecord = {
    key: makeKey(norm, type),
    shapedKey: norm.shapedKey,
    shapelessKey: norm.shapelessKey,
    type,
    ingredientIds,
    grid,
    resultId: id,
    resultItem: full,
    isVanilla: false,
    createdAt: now,
  };
  return { item: full, record };
}

// Helper to place a single item at a slot in the 9-cell grid.
function place(...entries: Array<[ItemId, SlotIndex]>): CraftingGrid {
  const grid: CraftingGrid = Array(9).fill(null);
  for (const [id, slot] of entries) grid[slot] = { itemId: id, count: 1 };
  return grid;
}

const SEEDS: SeedSpec[] = [
  {
    grid: place(['minecraft:furnace', 4], ['minecraft:coal', 5]),
    result: {
      fixedId: 'custom:seed:smelting_furnace',
      name: 'Smelting Furnace',
      description: 'A coal-fed furnace built for improved material processing.',
      textureId: 'minecraft:blast_furnace',
      textureReused: true,
      recipeType: 'shaped',
      category: 'machine',
      tags: ['heat', 'fuel', 'smelting', 'machine'],
      inheritedTraits: ['heat', 'processing', 'fuel'],
      newTraits: ['improved-smelting'],
      functionalTraits: ['smelting', 'fuel-burning'],
      rarity: 'uncommon',
      tier: 2,
      isUpgrade: true,
      isStructure: false,
      isMachine: true,
      model: 'seed',
      reasoning: 'Seed recipe',
      connectionExplanation: 'The furnace supplies the processing chamber while coal represents its built-in fuel system.',
      textureExplanation: 'The blast furnace texture is the closest vanilla texture to an upgraded smelting machine.',
    },
  },
  {
    grid: place(['minecraft:iron_helmet', 4], ['minecraft:torch', 1]),
    result: {
      fixedId: 'custom:seed:miners_helmet',
      name: "Miner's Helmet",
      description: 'An iron helmet fitted with a torch to light the way underground.',
      textureId: 'minecraft:iron_helmet',
      textureReused: true,
      recipeType: 'shaped',
      category: 'armor',
      tags: ['armor', 'head', 'light', 'mining'],
      inheritedTraits: ['armor', 'head', 'protection', 'light'],
      newTraits: ['headlamp'],
      functionalTraits: ['protection', 'illumination'],
      rarity: 'uncommon',
      tier: 2,
      isUpgrade: true,
      isStructure: false,
      isMachine: false,
      model: 'seed',
      connectionExplanation: 'The helmet protects the head while the torch mounted above provides light.',
      textureExplanation: 'The iron helmet texture is reused; the torch is implied by the headlamp concept.',
    },
  },
  {
    grid: place(['minecraft:iron_pickaxe', 4], ['minecraft:oak_door', 7]),
    result: {
      fixedId: 'custom:seed:mining_entrance',
      name: 'Mining Entrance',
      description: 'A reinforced doorframe leading down into a worked mine.',
      textureId: 'minecraft:stone_bricks',
      textureReused: true,
      recipeType: 'shaped',
      category: 'structure',
      tags: ['mining', 'access', 'entry', 'structure'],
      inheritedTraits: ['mining', 'tool', 'access', 'entry'],
      newTraits: ['entrance'],
      functionalTraits: ['access', 'mining'],
      rarity: 'common',
      tier: 2,
      isUpgrade: false,
      isStructure: true,
      isMachine: false,
      model: 'seed',
      connectionExplanation: 'The pickaxe implies excavation while the door below frames the entry into the mine.',
      textureExplanation: 'Stone bricks evoke a constructed mine entrance.',
    },
  },
  {
    grid: place(['minecraft:redstone', 4], ['minecraft:spyglass', 5]),
    result: {
      fixedId: 'custom:seed:redstone_scope',
      name: 'Redstone Scope',
      description: 'A spyglass wired with redstone to relay a magnified signal.',
      textureId: 'minecraft:spyglass',
      textureReused: true,
      recipeType: 'shaped',
      category: 'tool',
      tags: ['redstone', 'exploration', 'vision', 'tool'],
      inheritedTraits: ['redstone', 'signal', 'exploration', 'vision'],
      newTraits: ['signal-relay'],
      functionalTraits: ['vision', 'signal'],
      rarity: 'uncommon',
      tier: 2,
      isUpgrade: true,
      isStructure: false,
      isMachine: false,
      model: 'seed',
      connectionExplanation: 'The spyglass provides magnified vision while redstone carries its signal onward.',
      textureExplanation: 'The spyglass texture is the closest visual match.',
    },
  },
  {
    grid: place(['minecraft:compass', 4], ['minecraft:clock', 5]),
    result: {
      fixedId: 'custom:seed:navigation_watch',
      name: 'Navigation Watch',
      description: 'A combined compass and clock for orienting while traveling.',
      textureId: 'minecraft:clock',
      textureReused: true,
      recipeType: 'shaped',
      category: 'tool',
      tags: ['navigation', 'direction', 'time', 'tool'],
      inheritedTraits: ['navigation', 'direction', 'time'],
      newTraits: ['orientation'],
      functionalTraits: ['navigation', 'timekeeping'],
      rarity: 'uncommon',
      tier: 2,
      isUpgrade: false,
      isStructure: false,
      isMachine: false,
      model: 'seed',
      connectionExplanation: 'The compass points the way and the clock tracks the time of travel.',
      textureExplanation: 'The clock texture suits a worn navigation instrument.',
    },
  },
  {
    grid: place(['minecraft:slime_block', 7], ['minecraft:iron_boots', 4]),
    result: {
      fixedId: 'custom:seed:bouncing_boots',
      name: 'Bouncing Boots',
      description: 'Boots lined with slime that rebound the wearer on impact.',
      textureId: 'minecraft:iron_boots',
      textureReused: true,
      recipeType: 'shaped',
      category: 'armor',
      tags: ['armor', 'feet', 'bouncy', 'movement'],
      inheritedTraits: ['armor', 'feet', 'protection'],
      newTraits: ['bounce'],
      functionalTraits: ['protection', 'mobility'],
      rarity: 'rare',
      tier: 3,
      isUpgrade: true,
      isStructure: false,
      isMachine: false,
      model: 'seed',
      connectionExplanation: 'The boots carry the wearer while the slime block beneath grants rebound.',
      textureExplanation: 'Iron boots texture is reused; bounce is conveyed by the slime ingredient.',
    },
  },
  {
    grid: place(['minecraft:bow', 4], ['minecraft:fire_charge', 7]),
    result: {
      fixedId: 'custom:seed:flame_bow',
      name: 'Flame Bow',
      description: 'A bow imbued with a fire charge to ignite its arrows.',
      textureId: 'minecraft:bow',
      textureReused: true,
      recipeType: 'shaped',
      category: 'weapon',
      tags: ['combat', 'weapon', 'ranged', 'fire'],
      inheritedTraits: ['combat', 'weapon', 'ranged', 'fire'],
      newTraits: ['flame'],
      functionalTraits: ['ranged-combat', 'fire'],
      rarity: 'rare',
      tier: 3,
      isUpgrade: true,
      isStructure: false,
      isMachine: false,
      model: 'seed',
      connectionExplanation: 'The bow launches arrows while the fire charge below imbues them with flame.',
      textureExplanation: 'The bow texture is reused; the flame trait comes from the fire charge.',
    },
  },
  {
    grid: place(['minecraft:map', 4], ['minecraft:lodestone', 7]),
    result: {
      fixedId: 'custom:seed:bound_exploration_map',
      name: 'Bound Exploration Map',
      description: 'A map magically bound to a lodestone so it always tracks that point.',
      textureId: 'minecraft:filled_map',
      textureReused: true,
      recipeType: 'shaped',
      category: 'tool',
      tags: ['exploration', 'navigation', 'cartography', 'magic'],
      inheritedTraits: ['exploration', 'navigation', 'cartography'],
      newTraits: ['lodestone-bound'],
      functionalTraits: ['navigation', 'tracking'],
      rarity: 'rare',
      tier: 3,
      isUpgrade: true,
      isStructure: false,
      isMachine: false,
      model: 'seed',
      connectionExplanation: 'The map records terrain while the lodestone anchors it to a fixed location.',
      textureExplanation: 'The filled map texture suits a bound exploration chart.',
    },
  },
  {
    grid: place(['minecraft:fishing_rod', 4], ['minecraft:spyglass', 5]),
    result: {
      fixedId: 'custom:seed:surveyors_fishing_rod',
      name: "Surveyor's Fishing Rod",
      description: 'A fishing rod with a spyglass sight for casting at distant shoals.',
      textureId: 'minecraft:fishing_rod',
      textureReused: true,
      recipeType: 'shaped',
      category: 'tool',
      tags: ['fishing', 'exploration', 'vision', 'tool'],
      inheritedTraits: ['fishing', 'tool', 'rod', 'exploration', 'vision'],
      newTraits: ['long-cast'],
      functionalTraits: ['fishing', 'aim'],
      rarity: 'uncommon',
      tier: 2,
      isUpgrade: true,
      isStructure: false,
      isMachine: false,
      model: 'seed',
      connectionExplanation: 'The rod handles the catch while the spyglass aids targeting distant waters.',
      textureExplanation: 'The fishing rod texture is reused; the spyglass is implied.',
    },
  },
  {
    grid: place(['minecraft:barrel', 4], ['minecraft:hopper', 7]),
    result: {
      fixedId: 'custom:seed:sorting_barrel',
      name: 'Sorting Barrel',
      description: 'A barrel fitted with a hopper to collect and stow incoming items.',
      textureId: 'minecraft:barrel',
      textureReused: true,
      recipeType: 'shaped',
      category: 'machine',
      tags: ['storage', 'container', 'automation', 'redstone'],
      inheritedTraits: ['storage', 'container', 'transport', 'automation'],
      newTraits: ['auto-collect'],
      functionalTraits: ['storage', 'collection'],
      rarity: 'uncommon',
      tier: 2,
      isUpgrade: false,
      isStructure: false,
      isMachine: true,
      model: 'seed',
      connectionExplanation: 'The barrel stores goods while the hopper below funnels items in automatically.',
      textureExplanation: 'The barrel texture represents the storage body of the device.',
    },
  },
  {
    grid: place(['minecraft:redstone', 4], ['minecraft:oak_door', 7]),
    result: {
      fixedId: 'custom:seed:powered_door',
      name: 'Powered Door',
      description: 'A door wired to open and close on a redstone signal.',
      textureId: 'minecraft:oak_door',
      textureReused: true,
      recipeType: 'shaped',
      category: 'redstone',
      tags: ['redstone', 'access', 'entry', 'barrier', 'automation'],
      inheritedTraits: ['redstone', 'signal', 'access', 'entry', 'barrier'],
      newTraits: ['signal-operated'],
      functionalTraits: ['access-control'],
      rarity: 'uncommon',
      tier: 2,
      isUpgrade: true,
      isStructure: false,
      isMachine: false,
      model: 'seed',
      connectionExplanation: 'The door provides the barrier while redstone drives its opening mechanism.',
      textureExplanation: 'The oak door texture is reused; redstone conveys the powered behavior.',
    },
  },
  {
    grid: place(['minecraft:compass', 4], ['minecraft:map', 7]),
    result: {
      fixedId: 'custom:seed:explorers_map',
      name: "Explorer's Map",
      description: 'A map paired with a compass to keep the traveler oriented.',
      textureId: 'minecraft:filled_map',
      textureReused: true,
      recipeType: 'shaped',
      category: 'tool',
      tags: ['exploration', 'navigation', 'cartography', 'direction'],
      inheritedTraits: ['navigation', 'direction', 'exploration', 'cartography'],
      newTraits: ['oriented'],
      functionalTraits: ['navigation', 'mapping'],
      rarity: 'uncommon',
      tier: 2,
      isUpgrade: false,
      isStructure: false,
      isMachine: false,
      model: 'seed',
      connectionExplanation: 'The map records the surroundings while the compass keeps the explorer oriented.',
      textureExplanation: 'The filled map texture represents the combined chart.',
    },
  },
];

export function buildSeeds(): Array<{ item: GeneratedItem; record: RecipeRecord }> {
  return SEEDS.map((s) => build(s.grid, s.result));
}

export const SEED_COUNT = SEEDS.length;
